import React, { useState, useEffect } from "react"
import {
  Accordion as SharedAccordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
} from "@shared-components/Accordion/Accordion"
import {
  accordionContainerStyle,
  accordionItemStyles,
  textStyles,
  titleStyles,
} from "./styles"
import { AccordionIcon } from "@shared-components/AnimatableIcons/AccordionIcon"
import { motion } from "framer-motion"
import transitions from "@styles/design-tokens/09.transitions/transitions"
import { AccordionModule } from "../../carlsberg-headless/types/modules/Accordion"
import { Container } from "@shared-components/Container/Container"
import ParseHtmlToReact from "@shared-components/ParseHtmlToReact/ParseHtmlToReact"
import { BaseModule } from "../../carlsberg-headless/types/modules/util/base-module"

export interface AccordionProps extends BaseModule {
  cbKey: string
  accordions: AccordionModule[]
}

const toggleAccordion = (
  indices: number[],
  setIndices: React.Dispatch<number[]>,
  toggledIndex: number
) => {
  if (indices.includes(toggledIndex)) {
    setIndices(indices.filter(currentIndex => currentIndex !== toggledIndex))
  } else {
    setIndices([...indices, toggledIndex].sort())
  }
}

const getInitialActiveAccordions = (accordions: AccordionModule[]) => {
  if (!accordions) return []

  return accordions.reduce<number[]>((activeAccordions, accordion, index) => {
    if (accordion?.open) activeAccordions.push(index)

    return activeAccordions
  }, [])
}

const Accordion: React.FC<AccordionProps> = ({ accordions, cbKey }) => {
  const [indices, setIndices] = useState<number[]>([])

  useEffect(() => {
    setIndices(getInitialActiveAccordions(accordions))
  }, [accordions])

  if (!accordions) return null

  return (
    <Container key={cbKey} sx={accordionContainerStyle}>
      <SharedAccordion
        index={indices}
        onChange={value => toggleAccordion(indices, setIndices, value ?? 0)}
      >
        {accordions.map(({ headline, content, cbKey }, index) => {
          if (!headline || !content) return null
          return (
            <AccordionItem sx={accordionItemStyles} key={`${cbKey}-${index}`}>
              <AccordionButton sx={titleStyles}>
                {headline && <span>{headline}</span>}
                <AccordionIcon toggleIconState={indices.includes(index)} />
              </AccordionButton>

              <AccordionPanel sx={textStyles}>
                <motion.div
                  animate={indices.includes(index) ? "open" : "collapsed"}
                  transition={{
                    duration: 0.5,
                    ease: transitions.timingFunctions["in-out"],
                  }}
                  variants={{
                    open: { opacity: 1, height: "auto", visibility: "visible" },
                    collapsed: { opacity: 0, height: 0, visibility: "hidden" },
                  }}
                >
                  {content && <ParseHtmlToReact html={content} />}
                </motion.div>
              </AccordionPanel>
            </AccordionItem>
          )
        })}
      </SharedAccordion>
    </Container>
  )
}

export { Accordion }
